import { memo } from 'react';
import Markdown from 'react-markdown';
import type { Components, Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { markdownSanitizeSchema } from './markdownSanitizeSchema';
import classes from './MarkdownMessage.module.css';

/**
 * Renders an assistant turn as Markdown, plus the HTML the model embedded in it,
 * pruned against a safelist.
 *
 * ## Why there is an HTML path at all
 *
 * `TextWidget` renders LLM markdown with NO `rehypePlugins` and its docstring
 * says, in as many words, "do not add `rehype-raw`". That rule holds where it
 * was written: a report's text block has no reason to carry HTML. The chat does
 * — the product decision for this surface is that Markdown is the priority AND
 * embedded HTML must render — so the plugin arrives here together with the
 * sanitizer that makes it defensible, and never on its own.
 *
 * The other half of the house rule is untouched: **no `dangerouslySetInnerHTML`**.
 * `rehype-raw` parses the HTML into the hast AST and `rehype-sanitize` prunes
 * that AST; React still creates every node from the pruned tree. There are still
 * zero occurrences of `dangerouslySetInnerHTML` in web-app, and
 * `MarkdownMessage.test.tsx` asserts this file keeps it that way.
 *
 * ## Streaming: the sanitized-HTML path is only armed once the turn has settled
 *
 * Content arrives token by token (`message/appendChunk` concatenates onto
 * `content`), so this component re-parses a GROWING string on every chunk —
 * routinely mid-token, mid-fence and mid-tag. Two separate problems:
 *
 *  - `rehype-raw` runs a full HTML re-parse (serialize the tree, hand it to
 *    parse5, re-hydrate). Doing that per chunk is the expensive path, and a
 *    half-written `<table` or `<div class="` restructures everything after it,
 *    so already-rendered text visibly disappears and comes back as the tag
 *    completes.
 *  - The safelist is easiest to reason about when it applies to a FINISHED
 *    document rather than to a stream of truncated ones.
 *
 * So while `isStreaming` is true we render with `remark-gfm` only — which is
 * exactly `TextWidget`'s posture, where react-markdown v10 leaves raw HTML as
 * inert text — and the `rehype-raw` -> `rehype-sanitize` chain switches on when
 * the message settles (`isLoading` clears via `message/settleLoading` or
 * `message/setAssistantResponse`). Markdown itself renders live throughout;
 * react-markdown already tolerates unterminated `**`, an open fence or a
 * half-typed link by simply not applying the formatting yet.
 *
 * The security consequence is the one that matters: HTML can NEVER render
 * un-sanitized. Mid-stream it does not render at all; settled, it renders only
 * through the schema.
 */

/** Tables, strikethrough, autolinks, task lists — an LLM will emit all four. */
const REMARK_PLUGINS: Options['remarkPlugins'] = [ remarkGfm ];

/**
 * ORDER IS LOAD-BEARING. `rehype-raw` must run FIRST — it is what turns the raw
 * HTML sitting in the tree as text into real hast nodes — and `rehype-sanitize`
 * must run AFTER, over the nodes it produced. Swap them and the sanitizer walks
 * a tree where the HTML is still inert text, finds nothing to prune, and
 * `rehype-raw` then materialises it UNCHECKED: every rule in the safelist
 * bypassed, silently, with no error and no visible difference until someone
 * pastes a payload.
 */
const SETTLED_REHYPE_PLUGINS: Options['rehypePlugins'] = [
  rehypeRaw,
  [ rehypeSanitize, markdownSanitizeSchema ]
];

/**
 * Links leave the app in a new tab. `rel="noopener noreferrer"` is not optional
 * on a link whose href came from an untrusted string: without `noopener` the
 * opened page gets a live `window.opener` handle back into the app.
 *
 * The table override wraps every table — GFM or raw HTML — in its own
 * horizontal scroller, so a 9-column report never widens the chat bubble.
 */
const COMPONENTS: Components = {
  a: ({ node: _node, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  ),
  table: ({ node: _node, ...props }) => (
    <div className={classes.tableScroll}>
      <table {...props} />
    </div>
  )
};

export type MarkdownMessageProps = {
  /** The assistant's text. UNTRUSTED — see the docstring above. */
  content: string;
  /** True while tokens are still arriving for this message. */
  isStreaming?: boolean;
};

export const MarkdownMessage = memo(({ content, isStreaming = false }: MarkdownMessageProps ) => {
  // An assistant turn is created empty and filled by the stream; rendering an
  // empty prose box under the thinking chip would add a phantom gap.
  if ( !content ) return null;

  return (
    <div className={classes.markdown}>
      <Markdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={isStreaming ? undefined : SETTLED_REHYPE_PLUGINS}
        components={COMPONENTS}
      >
        {content}
      </Markdown>
    </div>
  );
});

MarkdownMessage.displayName = 'MarkdownMessage';
